import { useId, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { formatDate, formatNumber } from '../i18n.js';
import type { Severity } from './ui.js';

/**
 * The graphics of a site card on the start page (T-55a). Each chart answers hover, touch and the
 * arrow keys: the day under the pointer (or the focused one) is read out in a line below, so the
 * numbers never live in colour or a tooltip alone.
 */

const scoreTone = (score: number) =>
  score >= 90 ? 'text-ok' : score >= 65 ? 'text-warn' : 'text-bad';

/** Colour of a grade on the scale of the sheet (--g-a … --g-f). */
const GRADE_TONE: Readonly<Record<string, string>> = {
  A: 'text-g-a',
  B: 'text-g-b',
  C: 'text-g-c',
  D: 'text-g-d',
  E: 'text-g-e',
  F: 'text-g-f',
};

/** Ring with the Health Score; the grade in the middle. */
export function ScoreRing({
  score,
  grade,
  size = 'md',
}: {
  score: number | null;
  grade: string | null;
  /** "sm" and "xs": a row of a list (T-79; overview.html: 28 px), the grade only. */
  size?: 'md' | 'sm' | 'xs';
}) {
  const { t } = useTranslation();
  const r = 26;
  const length = 2 * Math.PI * r;
  const filled = score === null ? 0 : (Math.max(0, Math.min(100, score)) / 100) * length;
  return (
    <svg
      viewBox="0 0 64 64"
      className={`${size === 'xs' ? 'h-7 w-7' : size === 'sm' ? 'h-11 w-11' : 'h-16 w-16'} shrink-0 ${score === null || !grade ? 'text-fg-3' : (GRADE_TONE[grade] ?? scoreTone(score))}`}
      role="img"
      aria-label={
        score === null || !grade
          ? t('overview.card.noScore')
          : t('overview.card.scoreSr', { score, grade })
      }
    >
      <circle
        cx="32"
        cy="32"
        r={r}
        fill="none"
        stroke="currentColor"
        strokeOpacity="0.15"
        strokeWidth="7"
      />
      <circle
        cx="32"
        cy="32"
        r={r}
        fill="none"
        stroke="currentColor"
        strokeWidth="7"
        strokeLinecap="round"
        strokeDasharray={`${filled} ${length}`}
        transform="rotate(-90 32 32)"
      />
      {size !== 'md' ? (
        <text
          x="32"
          y="39"
          textAnchor="middle"
          className="fill-fg font-display text-[22px] font-extrabold"
        >
          {grade ?? '–'}
        </text>
      ) : (
        <>
          <text
            x="32"
            y="30"
            textAnchor="middle"
            className="fill-fg font-display text-[18px] font-extrabold"
          >
            {grade ?? '–'}
          </text>
          <text x="32" y="45" textAnchor="middle" className="fill-fg-3 font-mono text-[11px]">
            {score ?? ''}
          </text>
        </>
      )}
    </svg>
  );
}

/** Arrow keys move the highlighted point; Home/End jump to the ends. */
export function useActivePoint(count: number) {
  const [active, setActive] = useState<number | null>(null);
  const onKeyDown = (e: KeyboardEvent) => {
    // Alt/Ctrl/Cmd with an arrow are the browser's (back, forward); Escape lets the day go.
    if (count === 0 || e.altKey || e.ctrlKey || e.metaKey) return;
    if (e.key === 'Escape') {
      setActive(null);
      return;
    }
    const current = active ?? count - 1;
    const next =
      e.key === 'ArrowLeft'
        ? Math.max(0, current - 1)
        : e.key === 'ArrowRight'
          ? Math.min(count - 1, current + 1)
          : e.key === 'Home'
            ? 0
            : e.key === 'End'
              ? count - 1
              : null;
    if (next === null) return;
    e.preventDefault();
    setActive(next);
  };
  return { active, setActive, onKeyDown };
}

/**
 * Caption, chart and the line read out below it. Compact (a site card, overview.html): the caption
 * is for screen readers only and the line shows above the chart only while a day is picked.
 */
function ChartFrame({
  label,
  readout,
  readoutId,
  compact = false,
  picked = false,
  children,
}: {
  label: string;
  readout: ReactNode;
  readoutId: string;
  compact?: boolean;
  picked?: boolean;
  children: ReactNode;
}) {
  return (
    <figure className={compact ? 'relative min-w-0' : 'min-w-0'}>
      <figcaption className={compact ? 'sr-only' : 'lab mb-2'}>{label}</figcaption>
      {children}
      <p
        id={readoutId}
        aria-live="polite"
        className={
          !compact
            ? 'mt-1 min-h-5 font-mono text-[12.5px] text-fg-3'
            : picked
              ? 'absolute right-0 bottom-full mb-1 rounded border border-line bg-surface px-1.5 font-mono text-[12px] whitespace-nowrap text-fg-2'
              : 'sr-only'
        }
      >
        {readout}
      </p>
    </figure>
  );
}

/** Health Score of the last 30 days as a line with points to explore. */
export function HealthTrend({
  points,
  compact = false,
  muted = false,
}: {
  points: readonly { day: string; score: number }[];
  /** The thin line of a site card (overview.html: .spark), in the accent colour. */
  compact?: boolean;
  /** Grey, e.g. for a paused site. */
  muted?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const id = useId();
  const { active, setActive, onKeyDown } = useActivePoint(points.length);
  if (points.length < 2 && compact)
    return (
      <p className="flex h-[34px] items-center font-mono text-[12px] text-fg-3">
        {t('overview.card.trendEmpty')}
      </p>
    );
  if (points.length < 2)
    return (
      <ChartFrame
        label={t('overview.card.trend')}
        readout={t('overview.card.trendEmpty')}
        readoutId={id}
      >
        <div className="h-12 rounded bg-surface-2" aria-hidden="true" />
      </ChartFrame>
    );
  const w = 240;
  const h = compact ? 34 : 48;
  const min = Math.min(...points.map((p) => p.score), 50);
  const x = (i: number) => (i / (points.length - 1)) * (w - 8) + 4;
  const y = (v: number) => h - 4 - ((v - min) / (100 - min || 1)) * (h - 8);
  const shown = active ?? points.length - 1;
  const point = points[shown];
  const first = points[0]?.score ?? 0;
  const last = points[points.length - 1]?.score ?? 0;
  return (
    <ChartFrame
      label={t('overview.card.trend')}
      readoutId={id}
      compact={compact}
      picked={active !== null}
      readout={
        point
          ? t('overview.card.trendPoint', {
              date: formatDate(point.day, i18n.language),
              score: point.score,
            })
          : null
      }
    >
      <svg
        viewBox={`0 0 ${w} ${h}`}
        className={
          compact
            ? `spark block h-[34px] w-full cursor-crosshair ${muted ? 'text-fg-3' : 'text-acc'}`
            : `h-12 w-full cursor-crosshair rounded ${scoreTone(last)}`
        }
        preserveAspectRatio="none"
        role="img"
        tabIndex={0}
        aria-label={t('overview.card.trendSr', { from: first, to: last, days: points.length })}
        aria-describedby={id}
        onKeyDown={onKeyDown}
        onPointerLeave={() => setActive(null)}
      >
        <polyline
          points={points.map((p, i) => `${x(i)},${y(p.score)}`).join(' ')}
          fill="none"
          stroke="currentColor"
          strokeWidth={compact ? '1.5' : '2'}
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
        {point && (!compact || active !== null) ? (
          <circle cx={x(shown)} cy={y(point.score)} r="3.5" fill="currentColor" />
        ) : null}
        {points.map((p, i) => (
          <rect
            key={p.day}
            x={x(i) - w / points.length / 2}
            y="0"
            width={w / points.length}
            height={h}
            fill="transparent"
            onPointerEnter={() => setActive(i)}
          />
        ))}
      </svg>
    </ChartFrame>
  );
}

export interface UptimeDay {
  readonly day: string;
  readonly pct: number | null;
  readonly avgResponseMs: number | null;
}

/** A day of the 30-day strip (sheet: .ticks): full when fine, 70 % on a disrupted day, 30 % on an outage. */
const uptimeTick = (pct: number | null) =>
  pct === null ? 'n' : pct >= 99.9 ? '' : pct >= 99 ? 'w' : 'b';

/** The last 30 days as the day strokes of the sheet; days without data stay grey. */
/** Availability per day as the strokes of the sheet (.ticks): 30 days, or e.g. 90 on the status page. */
export function UptimeStrip({
  days,
  today,
  count = 30,
  compact = false,
}: {
  days: readonly UptimeDay[];
  today: Date;
  count?: number;
  /** The strokes alone on a site card (overview.html). */
  compact?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const id = useId();
  const byDay = new Map(days.map((d) => [d.day, d]));
  const all: UptimeDay[] = Array.from({ length: count }, (_, i) => {
    const day = new Date(today.getTime() - (count - 1 - i) * 86_400_000).toISOString().slice(0, 10);
    return byDay.get(day) ?? { day, pct: null, avgResponseMs: null };
  });
  const { active, setActive, onKeyDown } = useActivePoint(all.length);
  const shown = all[active ?? all.length - 1];
  const measured = all.filter((d) => d.pct !== null).length;
  return (
    <ChartFrame
      label={t('overview.card.uptime')}
      readoutId={id}
      compact={compact}
      picked={active !== null}
      readout={
        shown
          ? shown.pct === null
            ? t('overview.card.uptimeNone', { date: formatDate(shown.day, i18n.language) })
            : t('overview.card.uptimeDay', {
                date: formatDate(shown.day, i18n.language),
                pct: formatNumber(shown.pct, i18n.language, 2),
                ms: shown.avgResponseMs ?? '–',
              })
          : null
      }
    >
      <div
        role="img"
        tabIndex={0}
        aria-label={t('overview.card.uptimeSr', { days: measured })}
        aria-describedby={id}
        onKeyDown={onKeyDown}
        onPointerLeave={() => setActive(null)}
        className="ticks rounded"
        style={{ gridTemplateColumns: `repeat(${count}, 1fr)` }}
      >
        {all.map((d, i) => (
          <i
            key={d.day}
            onPointerEnter={() => setActive(i)}
            className={`${uptimeTick(d.pct)} ${i === active ? 'outline-2 outline-offset-1 outline-fg' : ''}`}
          />
        ))}
      </div>
    </ChartFrame>
  );
}

const SEVERITY_BAR: Readonly<Record<Severity, string>> = {
  critical: 'bg-bad',
  high: 'bg-g-e',
  medium: 'bg-warn',
  low: 'bg-neutral',
  info: 'bg-acc',
};

/**
 * Open problems as one stacked bar, with a link per severity to the filtered list. Bare: the bar
 * alone (overview.html: .probs .bar), its parts the links, named by their counts.
 */
export function SeverityBar({
  siteId,
  counts,
  order,
  bare = false,
}: {
  siteId: string;
  counts: Readonly<Record<Severity, number>>;
  order: readonly Severity[];
  bare?: boolean;
}) {
  const { t } = useTranslation();
  const total = order.reduce((n, s) => n + counts[s], 0);
  if (total === 0)
    return bare ? null : <p className="text-sm font-medium text-ok">{t('overview.noProblems')}</p>;
  const present = order.filter((s) => counts[s] > 0);
  if (bare)
    return (
      <div className="bar" role="group" aria-label={t('overview.card.bySeverity')}>
        {present.map((s) => {
          const name = t('overview.card.severityCount', {
            count: counts[s],
            severity: t(`severity.${s}`),
          });
          return (
            <Link
              key={s}
              to={`/sites/${siteId}/findings?severity=${s}`}
              className={SEVERITY_BAR[s]}
              style={{ width: `${(counts[s] / total) * 100}%` }}
              aria-label={name}
              title={name}
            />
          );
        })}
      </div>
    );
  return (
    <div>
      <div className="flex h-2.5 overflow-hidden rounded-full bg-surface-2" aria-hidden="true">
        {present.map((s) => (
          <span
            key={s}
            className={SEVERITY_BAR[s]}
            style={{ width: `${(counts[s] / total) * 100}%` }}
          />
        ))}
      </div>
      <ul className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-sm">
        {present.map((s) => (
          <li key={s}>
            <Link
              to={`/sites/${siteId}/findings?severity=${s}`}
              className="inline-flex items-center gap-1.5 rounded text-fg-2 no-underline underline-offset-2 hover:underline"
            >
              <span aria-hidden="true" className={`h-2.5 w-2.5 rounded-full ${SEVERITY_BAR[s]}`} />
              {t('overview.card.severityCount', { count: counts[s], severity: t(`severity.${s}`) })}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export interface UptimeHour {
  readonly t: string;
  readonly checks: number;
  readonly failed: number;
  readonly down: boolean;
}
export type HourState = 'ok' | 'degraded' | 'down' | 'none';

/** One hour: a confirmed outage, failed checks without an outage, all fine, or not checked. */
export const hourState = (h: UptimeHour): HourState =>
  h.down ? 'down' : h.checks === 0 ? 'none' : h.failed > 0 ? 'degraded' : 'ok';

const PEARL: Readonly<Record<HourState, string>> = {
  ok: 'bg-ok',
  degraded: 'bg-warn',
  down: 'bg-bad',
  none: 'bg-line-strong',
};

/** Share of successful checks over the hours, or null without checks. */
export function hoursUptime(hours: readonly UptimeHour[]): number | null {
  const checks = hours.reduce((n, h) => n + h.checks, 0);
  const failed = hours.reduce((n, h) => n + h.failed, 0);
  return checks > 0 ? Math.round(((checks - failed) / checks) * 10000) / 100 : null;
}

const BAR: Readonly<Record<HourState, string>> = { ok: '', degraded: 'w', down: 'b', none: 'n' };

/**
 * The last 24 hours, one mark per hour, oldest left (T-88): pearls, or the bars of a site card
 * (overview.html: .dots), always 24, hours without checks grey.
 */
export function PulseStrip({
  hours,
  bars = false,
}: {
  hours: readonly UptimeHour[];
  bars?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const count = (s: HourState) => hours.filter((h) => hourState(h) === s).length;
  const time = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { hour: '2-digit', minute: '2-digit' }).format(
      new Date(iso),
    );
  const label = t('overview.pulse.sr', {
    ok: count('ok'),
    degraded: count('degraded'),
    down: count('down'),
  });
  if (bars) {
    const last = hours.slice(-24);
    const missing = 24 - last.length;
    // Without hours (new, paused) only grey bars: the caption beside them says why.
    return (
      <div
        className="dots"
        {...(last.length > 0 ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })}
      >
        {Array.from({ length: missing }, (_, i) => (
          <i key={`none-${i}`} data-state="none" className="n" />
        ))}
        {last.map((h) => (
          <i
            key={h.t}
            data-state={hourState(h)}
            title={`${time(h.t)}: ${t(`overview.pulse.${hourState(h)}`)}`}
            className={BAR[hourState(h)]}
          />
        ))}
      </div>
    );
  }
  return (
    <div role="img" aria-label={label} className="flex items-center gap-[3px]">
      {hours.map((h) => (
        <span
          key={h.t}
          data-state={hourState(h)}
          title={`${time(h.t)}: ${t(`overview.pulse.${hourState(h)}`)}`}
          className={`size-2 shrink-0 rounded-full ${PEARL[hourState(h)]}`}
        />
      ))}
    </div>
  );
}
